import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import {
  GetRouteNetworksInAreaReq,
  GetRouteNetworksInAreaRes,
  RouterMaintainersResp,
} from '../interfaces/map-data';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MapDataHubService {
  private hubConnection!: HubConnection;

  // Retain last known payload so we can re-subscribe after reconnect
  private lastAreaPayload: GetRouteNetworksInAreaReq | undefined;

  private maintainersSubject = new BehaviorSubject<RouterMaintainersResp | null>(null);
  private networksSubject = new BehaviorSubject<GetRouteNetworksInAreaRes | null>(null);

  maintainers$: Observable<RouterMaintainersResp | null> = this.maintainersSubject.asObservable();
  networks$: Observable<GetRouteNetworksInAreaRes | null> = this.networksSubject.asObservable();

  /**
   * Builds the connection, registers ALL listeners, then starts.
   * Listeners are registered before .start() so no events are missed.
   */
  async startMapDataConnection(): Promise<void> {
    if (this.hubConnection) {
      // Already initialised — avoid creating duplicate connections
      return;
    }

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(environment.endpoints.viaTracksHub, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    // Register listeners BEFORE starting — no race condition
    this.registerListeners();

    // Re-invoke area subscription whenever SignalR auto-reconnects,
    //    because server-side state (SetMyCurrentArea) is lost on reconnect.
    this.hubConnection.onreconnected(async () => {
      if (this.lastAreaPayload !== undefined) {
        await this.invokeSetCurrentArea(this.lastAreaPayload);
      }
    });

    this.hubConnection.onclose((err) => {
      console.warn('[MapDataHub] Connection closed', err);
    });

    await this.hubConnection.start();
  }

  async setCurrentMapArea(payload: GetRouteNetworksInAreaReq | undefined): Promise<void> {
    this.lastAreaPayload = payload;

    await this.ensureConnected();
    await this.invokeSetCurrentArea(payload);
  }

  private registerListeners(): void {
    this.hubConnection.on('MaintainersUpdated', (maintainers: RouterMaintainersResp) => {
      this.maintainersSubject.next(maintainers);
    });

    this.hubConnection.on('NetworksUpdated', (networks: GetRouteNetworksInAreaRes) => {
      this.networksSubject.next(networks);
    });
  }

  /**
   * Waits for any in-progress connecting/reconnecting,
   * then starts the connection if it is disconnected.
   */
  private async ensureConnected(): Promise<void> {
    // Wait out transient states
    while (
      this.hubConnection.state === HubConnectionState.Connecting ||
      this.hubConnection.state === HubConnectionState.Reconnecting
    ) {
      await new Promise((res) => setTimeout(res, 50));
    }

    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      await this.hubConnection.start();
    }
  }

  private invokeSetCurrentArea(payload: GetRouteNetworksInAreaReq | undefined): Promise<void> {
    return this.hubConnection.invoke('SetMyCurrentArea', payload);
  }
}
